import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  createMissionBackupQuery,
  getMissionIsBackupableByIdQuery,
} from "./queries";

import { QUERY_STATUS } from "constants/constants";
import { canUserPerformAction } from "access-roles/general-functions";
import { useUserContext } from "api/user/UserProvider";
import { MISSION_BACKUP } from "access-roles/roles";
import { extractApiFieldErrorsFlat } from "utils/apiError";

const MissionBackupContext = createContext({});

// Remap API error codes to a single backup-specific message
const BACKUP_DATE_ERROR = {
  translationKey: "backup_dates_error",
  message:
    "Les dates doivent être comprises entre les dates de début et de fin de mission initiale. " +
    "Elles doivent également respecter l'ordre chronologique suivant :\n" +
    "• Date d'arrivée du consultant\n" +
    "• Date de début du remplacement\n" +
    "• Date de fin du remplacement\n" +
    "• Date de départ du consultant\n" +
    "Assurez-vous en amont que le consultant sélectionné est bien en IC à la date d'arrivée choisie.",
};

const BACKUP_ERROR_OVERRIDES = {
  original_start_ge_original_end: BACKUP_DATE_ERROR,
  end_date_before_start_date: BACKUP_DATE_ERROR,
  final_start_gt_backup_end: BACKUP_DATE_ERROR,
};

export const useMissionBackupContext = () => useContext(MissionBackupContext);

export const MissionBackupProvider = ({ ...props }) => {
  const { userRoleList, setApiErrorResponse } = useUserContext();

  const [missionBackupCreated, setMissionBackupCreated] = useState();
  const [missionIsBackupable, setMissionIsBackupable] = useState();
  const [missionBackupCreatedStatus, setMissionBackupCreatedStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [missionIsBackupableStatus, setMissionIsBackupableStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const createMissionBackup = useCallback(async ({ data }) => {
    setMissionBackupCreatedStatus(QUERY_STATUS.LOADING);
    setApiErrorResponse(null);
    try {
      const response = await createMissionBackupQuery({ data });
      setMissionBackupCreated(response);
      setMissionBackupCreatedStatus(QUERY_STATUS.SUCCESS);
    } catch (error) {
      console.error(
        "Mission backup creation error:",
        error?.response?.data?.["bad-request"]
      );
      setMissionBackupCreatedStatus(QUERY_STATUS.ERROR);
      const errors = extractApiFieldErrorsFlat(
        error?.response?.data?.["bad-request"]
      );
      Object.values(errors).forEach((err) => {
        const override = BACKUP_ERROR_OVERRIDES[err?.code];
        if (override) {
          err.translationKey = override.translationKey;
          err.message = override.message;
        }
      });
      setApiErrorResponse(errors);
    }
  }, []);

  const getMissionIsBackupableById = useCallback(
    async ({ id }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MISSION_BACKUP.ROLE_CREATE_MISSION_BACKUP,
        })
      ) {
        return;
      }
      try {
        setMissionIsBackupableStatus(QUERY_STATUS.LOADING);
        const response = await getMissionIsBackupableByIdQuery({ id });
        setMissionIsBackupable(response);
        setMissionIsBackupableStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setMissionIsBackupableStatus(QUERY_STATUS.ERROR);
        console.error(error);
      }
    },
    [userRoleList]
  );

  const values = useMemo(
    () => ({
      missionBackupCreated,
      setMissionBackupCreated,
      missionIsBackupable,
      setMissionIsBackupable,
      missionBackupCreatedStatus,
      setMissionBackupCreatedStatus,
      missionIsBackupableStatus,
      setMissionIsBackupableStatus,
      createMissionBackup,
      getMissionIsBackupableById,
    }),
    [
      missionBackupCreated,
      setMissionBackupCreated,
      missionIsBackupable,
      setMissionIsBackupable,
      missionBackupCreatedStatus,
      setMissionBackupCreatedStatus,
      missionIsBackupableStatus,
      setMissionIsBackupableStatus,
      createMissionBackup,
      getMissionIsBackupableById,
    ]
  );

  return <MissionBackupContext.Provider value={values} {...props} />;
};
